import { useEffect, useState } from 'react';
import { fetchWhy, type WhyView } from '../lib/api';
import { DocId } from '../lib/doc-id';
import { documentTypeLabel, labels, reasonClassLabel } from '../lib/labels';

/**
 * `#processing/why`: the `reasonClass` × type
 * breakdown, and the selectable document list *Send to review* (OPS-04) will act on, carrying each
 * document's detected `layout` as a column, not a second aggregate.
 *
 * An evidence panel, so it opens above the work list it explains and reads as one containment
 * layer: the two tables sit directly in the panel rather than in cards of their own.
 */
export function WhyPanel() {
  const [state, setState] = useState<'loading' | 'error' | WhyView>('loading');

  useEffect(() => {
    fetchWhy()
      .then(setState)
      .catch(() => setState('error'));
  }, []);

  if (state === 'loading') return <p className="notice">{labels.why.loading}</p>;
  if (state === 'error')
    return (
      <p className="notice notice--crit" role="alert">
        {labels.why.loadFailed}
      </p>
    );

  return (
    <section className="panel" aria-label={labels.why.heading}>
      <div className="panel__head">
        <h3>{labels.why.heading}</h3>
      </div>
      <BreakdownTable view={state} />
      <DocumentList view={state} />
    </section>
  );
}

function BreakdownTable({ view }: { view: WhyView }) {
  if (view.breakdown.length === 0 && view.unrecordedCount === 0)
    return <p className="panel__sub">{labels.processing.drainEmpty}</p>;
  return (
    <div className="responsive-table">
      <table>
        <thead>
          <tr>
            <th>{labels.why.breakdownColumns.reason}</th>
            <th className="num">{labels.why.breakdownColumns.count}</th>
          </tr>
        </thead>
        <tbody>
          {view.breakdown.map((row) => (
            <tr key={row.reasonClass}>
              <td>{reasonClassLabel(row.reasonClass)}</td>
              <td className="mono num">{row.total}</td>
            </tr>
          ))}
          {view.unrecordedCount > 0 && (
            <tr>
              <td>{labels.why.unrecorded}</td>
              <td className="mono num">{view.unrecordedCount}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DocumentList({ view }: { view: WhyView }) {
  if (view.documents.length === 0) return null;
  return (
    <div className="responsive-table flow-md">
      <table>
        <thead>
          <tr>
            <th>{labels.why.columns.document}</th>
            <th>{labels.why.columns.layout}</th>
            <th>{labels.why.columns.reason}</th>
          </tr>
        </thead>
        <tbody>
          {view.documents.map((doc) => (
            <tr key={doc.docId}>
              <td>
                <a href={`#doc/${doc.docId}`} aria-label={`${documentTypeLabel(doc.type)} · ${doc.docId}`}>
                  {documentTypeLabel(doc.type)} ·{' '}
                  <span className="mono">
                    <DocId id={doc.docId} />
                  </span>
                </a>
              </td>
              <td className="mono">{doc.layout ?? '—'}</td>
              <td>{doc.reasonClass ? reasonClassLabel(doc.reasonClass) : labels.why.unrecorded}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
